import { PublicJobDtoStatusEnum } from '@app/modules/audit/modules/aod/aod.types';
import {
  useAuditReportsGetOlafReprocessJobStatusByOrderId,
  useAuditReportsGetOlafReprocessJobStatusByReprocessKey,
  useAuditReportsTriggerReprocessOlafReprocess,
} from '@mi-front/api-v2';
import { useEffect, useRef } from 'react';

interface UseOrderReprocessProps {
  orders: string[];
  reprocessKey?: string;
  onReprocessComplete?: () => void;
  onReprocessError?: (error: Error) => void;
}

/**
 * Triggers an Olaf order reprocess and polls job status until the job is no longer pending.
 */
export function useOrderReprocess({
  orders,
  reprocessKey,
  onReprocessComplete,
  onReprocessError,
}: UseOrderReprocessProps) {
  // Join orders if multiple, otherwise return single order
  const currentOrders = orders.length > 1 ? orders.join(',') : orders[0] || '';

  // Track previous pending state to detect when the job finishes
  const previousIsPendingRef = useRef<boolean | undefined>(undefined);

  // Mutation for triggering reprocess
  const reprocessMutation = useAuditReportsTriggerReprocessOlafReprocess({
    mutation: {
      onError: (error: Error) => {
        onReprocessError?.(error);
      },
    },
  });

  // Shared query options
  const shouldEnableQuery = !reprocessMutation.submittedAt || reprocessMutation.isSuccess;
  const refetchInterval = (query: { state: { data?: { isPending?: boolean } } }) =>
    query.state.data?.isPending ? 2000 : false;

  // Query for checking job status - use reprocessKey if provided, otherwise use orderId
  const statusQueryByReprocessKey = reprocessKey
    ? useAuditReportsGetOlafReprocessJobStatusByReprocessKey(reprocessKey, {
        query: {
          enabled: Boolean(reprocessKey) && shouldEnableQuery,
          refetchInterval,
        },
      })
    : null;

  const statusQueryByOrderId = !reprocessKey
    ? useAuditReportsGetOlafReprocessJobStatusByOrderId(currentOrders, {
        query: {
          enabled: Boolean(currentOrders) && shouldEnableQuery,
          refetchInterval,
        },
      })
    : null;

  const statusQuery = statusQueryByReprocessKey || statusQueryByOrderId;

  // Detect transition from pending to completed so callers can refresh dependent data
  useEffect(() => {
    const currentIsPending = statusQuery?.data?.isPending;
    const previousIsPending = previousIsPendingRef.current;
    const currentStatus = statusQuery?.data?.status;

    if (
      previousIsPending === true &&
      currentIsPending === false &&
      currentStatus === PublicJobDtoStatusEnum.COMPLETED
    ) {
      onReprocessComplete?.();
    }

    if (currentIsPending !== undefined) {
      previousIsPendingRef.current = currentIsPending;
    }
  }, [statusQuery?.data?.isPending, statusQuery?.data?.status, onReprocessComplete]);

  return {
    reprocessMutation,
    statusQuery: statusQuery!,
    currentOrders,
    totalOrders: orders.length,
  };
}
